import type { SubmissionPresentationManifest } from "../../../shared/submission-document";

export type AdminRole = "SUPER_ADMIN" | "ADMIN";

export type AdminUser = {
  code: number;
  id: string;
  email: string;
  name: string;
  level: string;
  role: AdminRole;
};

export type AdminSubmissionSummary = {
  id: string;
  draftId: string;
  storeNameKo: string | null;
  contactEmail: string | null;
  category: {
    domain: string;
    categoryId: string;
    label: string;
  };
  adminAccountNeeded: boolean;
  adminLoginId: string | null;
  partnershipCode: number | null;
  partnershipCreationStatus: string | null;
  googleIntegrations: Array<"review" | "booking">;
  spotId: number | null;
  fileCount: number;
  submittedAt: string;
};

export type AdminSubmissionFile = {
  id: string;
  draftId: string;
  category: string;
  fieldId?: string;
  fileName: string;
  contentType: string;
  size: number;
  uploadedAt: string;
  adminDownloadPath: string;
  publicUrl?: string;
};

export type AdminSubmissionDetail = AdminSubmissionSummary & {
  data: Record<string, unknown>;
  files: AdminSubmissionFile[];
  presentationManifest?: SubmissionPresentationManifest;
};

export type AdminSubmissionVersionSourceKind = "INITIAL" | "PARTNER_REVISION" | "LEGACY_SNAPSHOT";

export type AdminSubmissionVersionSummary = {
  id: string;
  versionNumber: number;
  sourceKind: AdminSubmissionVersionSourceKind;
  revisionReason: string | null;
  sourceDraftVersion: number | null;
  formConfigVersionId: string | null;
  schemaHash: string | null;
  submittedAt: string;
  isLatest: boolean;
};

export type AdminSubmissionVersionDetail = AdminSubmissionVersionSummary & {
  submissionId: string;
  draftId: string;
  data: Record<string, unknown>;
  files: AdminSubmissionFile[];
  originalMarkdown: string;
  sourceWarning: string | null;
};

export type AdminSubmissionWorkingCopy = {
  markdown: string;
  revision: number;
  isInitialized: boolean;
  updatedBy: string | null;
  updatedByEmail: string | null;
  updatedAt: string | null;
};

export type AdminSubmissionWorkingRevision = {
  revision: number;
  markdown: string;
  eventKind: "AUTOSAVE" | "RESTORE";
  updatedBy: string;
  updatedByEmail: string;
  createdAt: string;
};

export type AdminSubmissionsResponse = {
  items: AdminSubmissionSummary[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

export type AdminSubmissionResponse = {
  submission: AdminSubmissionDetail;
};

export type PartnerAccountInput = {
  password?: string;
};

export type AdminInviteSummary = {
  id: string;
  draftId: string;
  path: string;
  categoryId: string | null;
  storeNameKo: string | null;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  submittedAt: string | null;
  submissionId: string | null;
  revisionRequested: boolean;
};

export type AdminInvitesResponse = {
  items: AdminInviteSummary[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

export type AdminApiToken = {
  id: string;
  name: string;
  tokenPrefix: string;
  tokenLast4: string;
  createdByEmail: string;
  createdByName: string;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

export type CreatedAdminApiToken = {
  token: AdminApiToken;
  rawToken: string;
};

export type AdminFormConfigState<TConfig = unknown> = {
  config: TConfig;
  updatedAt: string;
  updatedByEmail: string | null;
};

export type AdminFormConfigResponse<TConfig = unknown> = {
  draft: AdminFormConfigState<TConfig> | null;
  published: AdminFormConfigState<TConfig> | null;
};

export type AdminCategoryTaxonomyState<TConfig = unknown> = {
  config: TConfig;
  updatedAt: string;
  updatedByEmail: string | null;
};

export type AdminCategoryTaxonomyResponse<TConfig = unknown> = {
  draft: AdminCategoryTaxonomyState<TConfig> | null;
  published: AdminCategoryTaxonomyState<TConfig> | null;
};

export class WorkingCopyConflictError extends Error {
  constructor(public workingCopy: AdminSubmissionWorkingCopy) {
    super("다른 관리자가 먼저 작업용 수정본을 저장했습니다.");
    this.name = "WorkingCopyConflictError";
  }
}

export class AdminApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = "AdminApiError";
  }
}

export async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  const body = await response.json().catch(() => null) as { error?: string } | null;
  if (!response.ok) throw new AdminApiError(body?.error ?? `Request failed: ${response.status}`, response.status);
  return body as T;
}

export async function loginAdmin(identifier: string, password: string): Promise<AdminUser | { challengeId: string }> {
  const isEmail = identifier.includes("@");
  const response = await adminFetch<{ user?: AdminUser; challengeId?: string }>("/auth/login", {
    method: "POST",
    body: JSON.stringify(isEmail ? { email: identifier, password } : { id: identifier, password }),
  });
  // 2단계 인증을 켠 관리자는 세션 대신 코드 확인에 쓸 challengeId를 받는다.
  if (response.challengeId) return { challengeId: response.challengeId };
  return response.user as AdminUser;
}

export async function loginAdminWithTwoFactor(challengeId: string, code: string): Promise<AdminUser> {
  const response = await adminFetch<{ user: AdminUser }>("/auth/login/two-factor", {
    method: "POST",
    body: JSON.stringify({ challengeId, code }),
  });
  return response.user;
}

export async function fetchAdminMe(): Promise<AdminUser> {
  const response = await adminFetch<{ user: AdminUser }>("/auth/me", { signal: AbortSignal.timeout(8000) });
  return response.user;
}

export async function logoutAdmin(): Promise<void> {
  await adminFetch<{ success: boolean }>("/auth/logout", { method: "POST" });
}

export async function fetchAdminSubmissions(params: { page: number; pageSize: number; q?: string; categoryId?: string }): Promise<AdminSubmissionsResponse> {
  const search = new URLSearchParams({ page: String(params.page), pageSize: String(params.pageSize) });
  if (params.q?.trim()) search.set("q", params.q.trim());
  if (params.categoryId?.trim()) search.set("categoryId", params.categoryId.trim());
  return adminFetch<AdminSubmissionsResponse>(`/api/admin/submissions?${search.toString()}`);
}

export async function fetchAdminSubmission(id: string): Promise<AdminSubmissionDetail> {
  const response = await adminFetch<AdminSubmissionResponse>(`/api/admin/submissions/${encodeURIComponent(id)}`);
  return response.submission;
}

export type DuplicatedDraftInvite = {
  invite: { id: string; draftId: string; path: string; expiresAt: string };
  copiedFileCount: number;
};

export async function duplicateAdminSubmission(submissionId: string): Promise<DuplicatedDraftInvite> {
  return adminFetch<DuplicatedDraftInvite>(`/api/admin/submissions/${encodeURIComponent(submissionId)}/duplicate`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function duplicateAdminInvite(inviteId: string): Promise<DuplicatedDraftInvite> {
  return adminFetch<DuplicatedDraftInvite>(`/api/admin/invites/${encodeURIComponent(inviteId)}/duplicate`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function deleteAdminSubmission(submissionId: string): Promise<void> {
  await adminFetch<{ ok: boolean }>(`/api/admin/submissions/${encodeURIComponent(submissionId)}`, { method: "DELETE" });
}

export async function deleteAdminInvite(inviteId: string): Promise<void> {
  await adminFetch<{ ok: boolean }>(`/api/admin/invites/${encodeURIComponent(inviteId)}`, { method: "DELETE" });
}

function submissionVersionPath(submissionId: string, versionNumber: number): string {
  return `/api/admin/submissions/${encodeURIComponent(submissionId)}/versions/${versionNumber}`;
}

export async function fetchAdminSubmissionVersions(submissionId: string): Promise<AdminSubmissionVersionSummary[]> {
  const response = await adminFetch<{ versions: AdminSubmissionVersionSummary[] }>(`/api/admin/submissions/${encodeURIComponent(submissionId)}/versions`);
  return response.versions;
}

export async function fetchAdminSubmissionVersion(submissionId: string, versionNumber: number): Promise<AdminSubmissionVersionDetail> {
  const response = await adminFetch<{ version: AdminSubmissionVersionDetail }>(submissionVersionPath(submissionId, versionNumber));
  return response.version;
}

export async function fetchAdminSubmissionWorkingCopy(submissionId: string, versionNumber: number): Promise<AdminSubmissionWorkingCopy> {
  const response = await adminFetch<{ workingCopy: AdminSubmissionWorkingCopy }>(`${submissionVersionPath(submissionId, versionNumber)}/working-copy`);
  return response.workingCopy;
}

export async function saveAdminSubmissionWorkingCopy(submissionId: string, versionNumber: number, input: { expectedRevision: number; markdown: string }): Promise<AdminSubmissionWorkingCopy> {
  const response = await fetch(`${submissionVersionPath(submissionId, versionNumber)}/working-copy`, {
    method: "PATCH",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = await response.json().catch(() => null) as { error?: string; workingCopy?: AdminSubmissionWorkingCopy } | null;
  if (response.status === 409 && body?.workingCopy) throw new WorkingCopyConflictError(body.workingCopy);
  if (!response.ok || !body?.workingCopy) throw new AdminApiError(body?.error ?? `Request failed: ${response.status}`, response.status);
  return body.workingCopy;
}

export async function fetchAdminSubmissionWorkingRevisions(submissionId: string, versionNumber: number): Promise<AdminSubmissionWorkingRevision[]> {
  const response = await adminFetch<{ revisions: AdminSubmissionWorkingRevision[] }>(`${submissionVersionPath(submissionId, versionNumber)}/working-copy/revisions`);
  return response.revisions;
}

export function getAdminSubmissionMarkdownDownloadPath(submissionId: string, versionNumber: number, variant: "original" | "working"): string {
  return `${submissionVersionPath(submissionId, versionNumber)}/markdown?variant=${variant}&download=1`;
}

export async function revealAdminPassword(submissionId: string): Promise<string> {
  const response = await adminFetch<{ password: string }>(`/api/admin/submissions/${encodeURIComponent(submissionId)}/admin-password`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  return response.password;
}

export async function createPartnerAccount(submissionId: string, input: PartnerAccountInput): Promise<{ code: number }> {
  const response = await adminFetch<{ partner: { code: number } }>(`/api/admin/submissions/${encodeURIComponent(submissionId)}/partner-account`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.partner;
}

export async function updateSubmissionSpot(submissionId: string, spotId: number | null): Promise<number | null> {
  const response = await adminFetch<{ spotId: number | null }>(`/api/admin/submissions/${encodeURIComponent(submissionId)}/spot`, {
    method: "PUT",
    body: JSON.stringify({ spotId }),
  });
  return response.spotId;
}

export type CreatedPartnership = {
  code: number;
  companyName: string;
  alreadyCreated: boolean;
};

export async function createPartnership(submissionId: string): Promise<CreatedPartnership> {
  const response = await adminFetch<{ partnership: CreatedPartnership }>(`/api/admin/submissions/${encodeURIComponent(submissionId)}/partnership`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  return response.partnership;
}

export type RequestSubmissionRevisionInput = {
  reason: string;
  rotateLink?: boolean;
};

export type RequestSubmissionRevisionResult = {
  id: string;
  path: string;
  reason?: string;
  draftVersion?: number;
  expiresAt: string;
  updatedAt: string;
};

export async function requestSubmissionRevision(submissionId: string, input: RequestSubmissionRevisionInput): Promise<RequestSubmissionRevisionResult> {
  const body: RequestSubmissionRevisionInput = { reason: input.reason.trim() };
  if (input.rotateLink !== undefined) body.rotateLink = input.rotateLink;
  const response = await adminFetch<{ invite: RequestSubmissionRevisionResult }>(`/api/admin/submissions/${encodeURIComponent(submissionId)}/revision-invite`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return response.invite;
}

export async function fetchAdminInvites(params: { page: number; pageSize: number }): Promise<AdminInvitesResponse> {
  const search = new URLSearchParams({ page: String(params.page), pageSize: String(params.pageSize) });
  return adminFetch<AdminInvitesResponse>(`/api/admin/invites?${search.toString()}`);
}

export async function fetchAdminApiTokens(): Promise<AdminApiToken[]> {
  const response = await adminFetch<{ tokens: AdminApiToken[] }>("/api/admin/api-tokens");
  return response.tokens;
}

export async function createAdminApiToken(input: { name: string; expiresInDays: 30 | 90 | 180 | 365 | null }): Promise<CreatedAdminApiToken> {
  return adminFetch<CreatedAdminApiToken>("/api/admin/api-tokens", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function revokeAdminApiToken(id: string): Promise<void> {
  await adminFetch<{ ok: boolean }>(`/api/admin/api-tokens/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function fetchAdminFormConfig<TConfig>(): Promise<AdminFormConfigResponse<TConfig>> {
  return adminFetch<AdminFormConfigResponse<TConfig>>("/api/admin/form-config");
}

export async function saveAdminFormConfigDraft<TConfig>(config: TConfig): Promise<AdminFormConfigState<TConfig>> {
  const response = await adminFetch<{ draft: AdminFormConfigState<TConfig> }>("/api/admin/form-config/draft", {
    method: "PUT",
    body: JSON.stringify({ config }),
  });
  return response.draft;
}

export async function publishAdminFormConfig<TConfig>(config: TConfig): Promise<AdminFormConfigState<TConfig>> {
  const response = await adminFetch<{ published: AdminFormConfigState<TConfig> }>("/api/admin/form-config/publish", {
    method: "POST",
    body: JSON.stringify({ config }),
  });
  return response.published;
}

export async function fetchAdminCategoryTaxonomy<TConfig>(): Promise<AdminCategoryTaxonomyResponse<TConfig>> {
  return adminFetch<AdminCategoryTaxonomyResponse<TConfig>>("/api/admin/category-taxonomy");
}

export async function saveAdminCategoryTaxonomyDraft<TConfig>(config: TConfig): Promise<AdminCategoryTaxonomyState<TConfig>> {
  const response = await adminFetch<{ draft: AdminCategoryTaxonomyState<TConfig> }>("/api/admin/category-taxonomy/draft", {
    method: "PUT",
    body: JSON.stringify({ config }),
  });
  return response.draft;
}

export async function publishAdminCategoryTaxonomy<TConfig>(config: TConfig): Promise<AdminCategoryTaxonomyState<TConfig>> {
  const response = await adminFetch<{ published: AdminCategoryTaxonomyState<TConfig> }>("/api/admin/category-taxonomy/publish", {
    method: "POST",
    body: JSON.stringify({ config }),
  });
  return response.published;
}
